// ============================================================================
// OverlappingZones ViewModel
// ============================================================================

import { useState, useCallback, useMemo } from "react";
import type {
	OverlappingZonesProps,
	OverlappingZonesViewModel,
	MapFeatureData,
	DeleteConfirmation,
	BulkDeleteOption,
} from "./OverlappingZones.types";
import type { OverlapGroup } from "../../WizardContainer";
import { DUPLICATE_GROUP_COLORS, DUPLICATE_GROUP_COLOR_NAMES } from "../../shared/constants";
import { formatArea } from "../../shared";

// Zone hatch angles (opposite directions)
const ZONE_HATCH_ANGLES = [45, 135, 90, 0];

/**
 * ViewModel hook for OverlappingZones component
 */
export function useOverlappingZonesViewModel(props: OverlappingZonesProps): OverlappingZonesViewModel {
	const { issues, currentIssueIndex, skippedIssueIds, onDeleteFeature, onDeleteFeatures } = props;

	// Track which features are hidden on the map
	const [hiddenFeatureIds, setHiddenFeatureIds] = useState<Set<string>>(new Set());
	// State for delete confirmation dialog (single or bulk)
	const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation | null>(null);

	// Derived state
	const hasIssues = issues.length > 0;
	const currentIssue = hasIssues ? (issues[currentIssueIndex] ?? null) : null;
	const isSkipped = currentIssue ? skippedIssueIds.has(currentIssue.id) : false;

	// Create unique feature ID
	const createFeatureId = useCallback((groupId: string, featureIndex: number): string => {
		return `${groupId}-${featureIndex}`;
	}, []);

	// Toggle feature visibility
	const toggleFeatureVisibility = useCallback((featureId: string) => {
		setHiddenFeatureIds((prev) => {
			const next = new Set(prev);
			if (next.has(featureId)) {
				next.delete(featureId);
			} else {
				next.add(featureId);
			}
			return next;
		});
	}, []);

	// Show all features
	const showAllFeatures = useCallback(() => {
		setHiddenFeatureIds(new Set());
	}, []);

	// Hide all features
	const hideAllFeatures = useCallback(() => {
		if (!currentIssue) return;
		const allIds = new Set<string>();
		for (const group of currentIssue.overlapGroups) {
			for (let i = 0; i < group.features.length; i++) {
				allIds.add(createFeatureId(group.id, group.featureIndices[i]));
			}
		}
		setHiddenFeatureIds(allIds);
	}, [currentIssue, createFeatureId]);

	// Get display name for a feature
	const getFeatureDisplayName = useCallback((group: OverlapGroup, index: number): string => {
		const feature = group.features[index];
		const featureIndex = group.featureIndices[index];

		// Try to get a name from properties
		const props = feature.properties;
		if (props) {
			for (const field of ["Zone", "Name", "name", "NAME", "id", "Id", "ID", "SAMPLE_ID", "SampleID"]) {
				if (props[field]) {
					return String(props[field]);
				}
			}
		}

		return `Zone #${featureIndex + 1}`;
	}, []);

	// Get formatted overlap area for a group
	const getGroupOverlapArea = useCallback((group: OverlapGroup): string => {
		return formatArea(group.intersectionArea);
	}, []);

	// Get color for a position within group
	const getPositionColor = useCallback((positionIndex: number): string => {
		return DUPLICATE_GROUP_COLORS[positionIndex % DUPLICATE_GROUP_COLORS.length];
	}, []);

	// Generate map features data
	const mapFeatures = useMemo((): MapFeatureData[] => {
		if (!currentIssue) return [];

		const features: MapFeatureData[] = [];

		currentIssue.overlapGroups.forEach((group) => {
			group.features.forEach((feature, i) => {
				const featureIndex = group.featureIndices[i];
				const color = getPositionColor(i);
				features.push({
					feature,
					groupId: group.id,
					featureIndex,
					isDuplicate: true,
					color,
					hatchAngle: ZONE_HATCH_ANGLES[i % ZONE_HATCH_ANGLES.length],
				});
			});
		});

		return features;
	}, [currentIssue, getPositionColor]);

	// ── Bulk delete by position/color ──────────────────────────────────────
	const bulkDeleteByPosition = useMemo((): BulkDeleteOption[] => {
		if (!currentIssue) return [];

		const maxPositions = Math.max(...currentIssue.overlapGroups.map((g) => g.features.length), 0);
		if (maxPositions < 2) return [];

		const options: BulkDeleteOption[] = [];
		for (let pos = 0; pos < maxPositions; pos++) {
			const zoneIndices: number[] = [];

			for (const group of currentIssue.overlapGroups) {
				if (pos < group.features.length) {
					zoneIndices.push(group.featureIndices[pos]);
				}
			}

			if (zoneIndices.length === 0) continue;

			const colorName = DUPLICATE_GROUP_COLOR_NAMES[pos % DUPLICATE_GROUP_COLOR_NAMES.length];
			const color = getPositionColor(pos);

			options.push({
				key: `pos-${pos}`,
				label: colorName,
				color,
				pointIndices: [],
				zoneIndices,
				count: zoneIndices.length,
			});
		}

		return options;
	}, [currentIssue, getPositionColor]);

	// ── Bulk delete by file ────────────────────────────────────────────────
	const bulkDeleteByFile = useMemo((): BulkDeleteOption[] => {
		if (!currentIssue) return [];

		const fileMap = new Map<string, number[]>();

		for (const group of currentIssue.overlapGroups) {
			for (let i = 0; i < group.features.length; i++) {
				const filePath = group.features[i].properties?.FilePath;
				if (!filePath) continue;

				if (!fileMap.has(filePath)) {
					fileMap.set(filePath, []);
				}
				fileMap.get(filePath)!.push(group.featureIndices[i]);
			}
		}

		if (fileMap.size < 2) return [];

		const options: BulkDeleteOption[] = [];
		for (const [filePath, zoneIndices] of fileMap) {
			const fileName = filePath.split("/").pop() || filePath;
			options.push({
				key: `file-${filePath}`,
				label: fileName,
				pointIndices: [],
				zoneIndices,
				count: zoneIndices.length,
			});
		}

		return options;
	}, [currentIssue]);

	// ── Delete handlers ────────────────────────────────────────────────────

	const handleDeleteFeatureClick = useCallback((groupId: string, featureIndex: number) => {
		setDeleteConfirmation({ kind: "single", groupId, featureIndex });
	}, []);

	const handleBulkDeleteClick = useCallback((option: BulkDeleteOption) => {
		setDeleteConfirmation({ kind: "bulk", option });
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (!deleteConfirmation || !currentIssue) return;

		if (deleteConfirmation.kind === "single") {
			onDeleteFeature(currentIssue.job.id, "zone", deleteConfirmation.featureIndex);
		} else {
			const { pointIndices, zoneIndices } = deleteConfirmation.option;
			onDeleteFeatures(currentIssue.job.id, pointIndices, zoneIndices);
		}

		setDeleteConfirmation(null);
	}, [deleteConfirmation, currentIssue, onDeleteFeature, onDeleteFeatures]);

	const handleCancelDelete = useCallback(() => {
		setDeleteConfirmation(null);
	}, []);

	// ── Dialog text computed from confirmation state ────────────────────────
	const deleteDialogTitle = useMemo(() => {
		if (!deleteConfirmation) return "";
		if (deleteConfirmation.kind === "single") return "Delete Zone";
		return `Delete ${deleteConfirmation.option.count} Zones`;
	}, [deleteConfirmation]);

	const deleteDialogMessage = useMemo(() => {
		if (!deleteConfirmation) return "";
		if (deleteConfirmation.kind === "single") {
			return "Are you sure you want to delete this zone? This action cannot be undone.";
		}
		const { label, count } = deleteConfirmation.option;
		return `Are you sure you want to delete all ${count} "${label}" zone${count !== 1 ? "s" : ""} across all groups? This action cannot be undone.`;
	}, [deleteConfirmation]);

	return {
		hasIssues,
		currentIssue,
		isSkipped,
		hiddenFeatureIds,
		toggleFeatureVisibility,
		showAllFeatures,
		hideAllFeatures,
		deleteConfirmation,
		handleDeleteFeatureClick,
		handleBulkDeleteClick,
		handleConfirmDelete,
		handleCancelDelete,
		deleteDialogTitle,
		deleteDialogMessage,
		bulkDeleteByPosition,
		bulkDeleteByFile,
		getFeatureDisplayName,
		getGroupOverlapArea,
		mapFeatures,
		getPositionColor,
	};
}
